import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import { Line, Pie } from 'react-chartjs-2';
import { AdblockDailyMetrics } from '../../../types/adblock';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Skeleton } from '../../ui/skeleton';

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface AdblockChartsProps {
  dailyMetrics: AdblockDailyMetrics[];
  isLoading: boolean;
  theme: 'light' | 'dark';
}

export function AdblockCharts({ dailyMetrics, isLoading, theme }: AdblockChartsProps) {
  const { t } = useTranslation();

  const isDark = theme === 'dark';
  const textColor = isDark ? '#e5e7eb' : '#374151';
  const gridColor = isDark ? '#374151' : '#e5e7eb';

  // Configuration commune pour les graphiques
  const commonOptions: Partial<ChartOptions> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false // Supprime les labels puisqu'on a le titre dans la card
      }
    },
    scales: {
      x: {
        ticks: {
          color: textColor,
          maxTicksLimit: 4 // Maximum 4 dates sur l'axe X
        },
        grid: {
          color: gridColor
        }
      },
      y: {
        beginAtZero: true, // Commence à 0
        ticks: {
          color: textColor
        },
        grid: {
          color: gridColor
        }
      }
    }
  };

  // Préparer les données pour les graphiques
  const labels = dailyMetrics.map(metric => 
    new Date(metric.date).toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' })
  );

  // Données pour les graphiques en ligne
  const createLineData = (data: number[], color: string) => ({
    labels,
    datasets: [
      {
        data,
        borderColor: color,
        backgroundColor: color + '20',
        tension: 0.4,
        fill: true
      }
    ]
  });

  // Données pour le graphique en secteurs (Adblock Ratio)
  const blockingRateData = {
    labels: [
      t('adblock.dashboard.charts.adblockPageviews'),
      t('adblock.dashboard.charts.normalPageviews')
    ],
    datasets: [
      {
        data: dailyMetrics.length > 0 ? [
          dailyMetrics[dailyMetrics.length - 1]?.blockingRate || 0,
          100 - (dailyMetrics[dailyMetrics.length - 1]?.blockingRate || 0)
        ] : [0, 100],
        backgroundColor: ['#ef4444', '#22c55e'],
        borderWidth: 2,
        borderColor: isDark ? '#374151' : '#ffffff'
      }
    ]
  };

  // Fonction utilitaire pour formater les nombres
  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('fr-FR').format(value);
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const renderChart = (title: string, children: React.ReactNode, metric?: { value: number; type: 'sum' | 'average' | 'currency' | 'percentage' }) => (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-bold flex justify-between items-center">
          <span>{title}</span>
          {metric && (
            <span className="text-sm font-bold text-foreground">
              {metric.type === 'currency' ? formatCurrency(metric.value) : 
               metric.type === 'percentage' ? `${metric.value.toFixed(1)}%` :
               formatNumber(metric.value)}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          {isLoading ? (
            <Skeleton className="w-full h-full" />
          ) : (
            children
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (dailyMetrics.length === 0 && !isLoading) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Première ligne de graphiques - Total, Adblock, Ratio */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {renderChart(
          t('adblock.dashboard.charts.totalPageviews'),
          <Line 
            data={createLineData(
              dailyMetrics.map(d => d.pageviews), 
              '#3b82f6'
            )} 
            options={commonOptions as ChartOptions<'line'>}
          />,
          { 
            value: dailyMetrics.reduce((sum, d) => sum + d.pageviews, 0), 
            type: 'sum' 
          }
        )}

        {renderChart(
          t('adblock.dashboard.charts.adblockPageviews'),
          <Line
            data={createLineData(
              dailyMetrics.map(m => m.pageviewsBlockingAds),
              '#ef4444'
            )}
            options={commonOptions as ChartOptions<'line'>}
          />,
          { 
            value: dailyMetrics.reduce((sum, m) => sum + m.pageviewsBlockingAds, 0), 
            type: 'sum' 
          }
        )}

        {renderChart(
          t('adblock.dashboard.charts.adblockRatio'),
          <Pie
            data={blockingRateData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: {
                    color: textColor,
                    padding: 20
                  }
                }
              }
            }}
          />,
          { 
            value: dailyMetrics.length > 0 ? 
              (dailyMetrics.reduce((sum, m) => sum + m.pageviewsBlockingAds, 0) / 
               dailyMetrics.reduce((sum, m) => sum + m.pageviews, 0)) * 100 : 0, 
            type: 'percentage' 
          }
        )}
      </div>

      {/* Deuxième ligne de graphiques - CPM, Impressions, Revenue, Page RPM */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {renderChart(
          t('adblock.dashboard.charts.cpm'),
          <Line
            data={createLineData(
              dailyMetrics.map(m => m.impressions > 0 ? (m.revenueUSD / m.impressions) * 1000 : 0),
              '#22c55e'
            )}
            options={commonOptions as ChartOptions<'line'>}
          />,
          { 
            value: dailyMetrics.length > 0 ? 
              dailyMetrics.reduce((sum, m) => sum + (m.impressions > 0 ? (m.revenueUSD / m.impressions) * 1000 : 0), 0) / dailyMetrics.length : 0, 
            type: 'currency' 
          }
        )}

        {renderChart(
          t('adblock.dashboard.charts.impressions'),
          <Line
            data={createLineData(
              dailyMetrics.map(m => m.impressions),
              '#8b5cf6'
            )}
            options={commonOptions as ChartOptions<'line'>}
          />,
          { 
            value: dailyMetrics.reduce((sum, m) => sum + m.impressions, 0), 
            type: 'sum' 
          }
        )}

        {renderChart(
          t('adblock.dashboard.charts.grossRevenue'),
          <Line
            data={createLineData(
              dailyMetrics.map(m => m.revenueUSD),
              '#f59e0b'
            )}
            options={commonOptions as ChartOptions<'line'>}
          />,
          { 
            value: dailyMetrics.reduce((sum, m) => sum + m.revenueUSD, 0), 
            type: 'currency' 
          }
        )}

        {renderChart(
          t('adblock.dashboard.charts.pageRpm'),
          <Line
            data={createLineData(
              dailyMetrics.map(m => m.pageviewsBlockingAds > 0 ? (m.revenueUSD / m.pageviewsBlockingAds) * 1000 : 0),
              '#06b6d4'
            )}
            options={commonOptions as ChartOptions<'line'>}
          />,
          { 
            value: dailyMetrics.length > 0 ? 
              dailyMetrics.reduce((sum, m) => sum + (m.pageviewsBlockingAds > 0 ? (m.revenueUSD / m.pageviewsBlockingAds) * 1000 : 0), 0) / dailyMetrics.length : 0, 
            type: 'currency' 
          }
        )}
      </div>
    </div>
  );
}